import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Trash2, Pencil, Sparkles, ArrowRight, ArrowLeft, LogOut, DollarSign, Package, ShoppingCart } from "lucide-react";
import { API } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import NewsletterAdmin from "@/components/admin/NewsletterAdmin";
import RingAdmin from "@/components/admin/RingAdmin";
import JourneysAdmin from "@/components/admin/JourneysAdmin";
import HeroAdmin from "@/components/admin/HeroAdmin";
import PollAdmin from "@/components/admin/PollAdmin";
import SearchesAdmin from "@/components/admin/SearchesAdmin";
import QuickPlaysAdmin from "@/components/admin/QuickPlaysAdmin";
import StarterKitAdmin from "@/components/admin/starterkit/StarterKitAdmin";
import MediaLibrary from "@/components/admin/MediaLibrary";
import FunnelAdmin from "@/components/admin/FunnelAdmin";
import TestimonialsAdmin from "@/components/admin/TestimonialsAdmin";
import RookieAdmin from "@/components/admin/RookieAdmin";
import { markInternalDevice } from "@/lib/attribution";
import ImageUpload from "@/components/admin/ImageUpload";
import { SERIES } from "@/lib/series";

const CATEGORIES = ["Coaches Corner", "Parent Sideline", "Practice Plans"];
const SERIES_OPTS = ["None", ...SERIES.map((s) => s.name)];
const FORMAT_OPTS = ["Read", "Watch"];
const KEY_STORE = "voluntold_admin_key";

function adminApi(key) {
  return axios.create({ baseURL: API, headers: { "X-Admin-Key": key } });
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function nextWeekday(target) {
  // target: 2 = Tue, 5 = Fri (JS getDay: Sun0..Sat6)
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() !== target);
  return ymd(d);
}
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("write");
  const [queueCount, setQueueCount] = useState(0);
  const [reviewProgress, setReviewProgress] = useState({ reviewed: 0, total: 0 });
  const [scheduledCount, setScheduledCount] = useState(0);

  useEffect(() => {
    if (key) tryLogin(key, true);
    // eslint-disable-next-line
  }, []);

  const tryLogin = async (k, silent = false) => {
    try {
      await adminApi(k).post("/admin/login");
      localStorage.setItem(KEY_STORE, k);
      markInternalDevice();
      setAuthed(true);
      loadCounts(k);
    } catch {
      if (!silent) toast.error("Wrong passcode.");
      setAuthed(false);
    }
  };

  const loadCounts = async (k = key) => {
    try {
      const [q, s] = await Promise.all([
        adminApi(k).get("/admin/review-queue"),
        adminApi(k).get("/admin/scheduled"),
      ]);
      setQueueCount(q.data.count);
      setReviewProgress({ reviewed: q.data.reviewed || 0, total: q.data.total || 0 });
      setScheduledCount(s.data.count);
    } catch (e) { console.error("Failed to load admin counts:", e); }
  };

  const logout = () => { localStorage.removeItem(KEY_STORE); setAuthed(false); setKey(""); };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 pt-24" data-testid="admin-login">
        <span className="kicker">Admin</span>
        <h1 className="mt-2 font-head font-semibold uppercase text-3xl text-[#1E1E1E]">Scout's Desk</h1>
        <p className="mt-2 text-[#6E6E6E] text-sm">Enter the passcode to write and review.</p>
        <form onSubmit={(e) => { e.preventDefault(); tryLogin(key); }} className="mt-6 flex">
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input"
            className="flex-1 border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="Passcode" />
          <button type="submit" data-testid="admin-login-btn" className="bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-sm px-6 hover:bg-[#242424] transition-colors">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-12" data-testid="admin-page">
      <div className="flex items-center justify-between border-b-2 border-[#1E1E1E] pb-4">
        <div>
          <span className="kicker">Admin</span>
          <div className="flex items-center gap-3">
            <img src="/brand/logos/voluntoldhq-monogram-on-light.svg" alt="VoluntoldHQ" className="h-7 w-auto" />
            <h1 className="font-head font-semibold uppercase text-3xl text-[#1E1E1E]">Scout's Desk</h1>
          </div>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-[#6E6E6E] hover:text-[#1E1E1E] text-sm font-head uppercase tracking-wide"><LogOut size={16} /> Exit</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setTab("write")} data-testid="admin-tab-write"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "write" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>New Article</button>
        <button onClick={() => { setTab("scheduled"); loadCounts(); }} data-testid="admin-tab-scheduled"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "scheduled" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Scheduled ({scheduledCount})
        </button>
        <button onClick={() => { setTab("review"); loadCounts(); }} data-testid="admin-tab-review"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "review" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Review Queue ({queueCount})
        </button>
        <button onClick={() => setTab("purchases")} data-testid="admin-tab-purchases"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "purchases" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Purchases
        </button>
        <button onClick={() => setTab("downloads")} data-testid="admin-tab-downloads"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "downloads" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Downloads
        </button>
        <button onClick={() => setTab("newsletter")} data-testid="admin-tab-newsletter"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "newsletter" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Newsletter
        </button>
        <button onClick={() => setTab("ring")} data-testid="admin-tab-ring"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "ring" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Ring
        </button>
        <button onClick={() => setTab("poll")} data-testid="admin-tab-poll"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "poll" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Hot Takes
        </button>
        <button onClick={() => setTab("journeys")} data-testid="admin-tab-journeys"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "journeys" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Journeys
        </button>
        <button onClick={() => setTab("hero")} data-testid="admin-tab-hero"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "hero" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Hero
        </button>
        <button onClick={() => setTab("searches")} data-testid="admin-tab-searches"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "searches" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Searches
        </button>
        <button onClick={() => setTab("quickplays")} data-testid="admin-tab-quickplays"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "quickplays" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Quick Plays
        </button>
        <button onClick={() => setTab("starterkit")} data-testid="admin-tab-starterkit"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "starterkit" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Starter Kit
        </button>
        <button onClick={() => setTab("media")} data-testid="admin-tab-media"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "media" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Media
        </button>
        <button onClick={() => setTab("funnel")} data-testid="admin-tab-funnel"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "funnel" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Funnel
        </button>
        <button onClick={() => setTab("testimonials")} data-testid="admin-tab-testimonials"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "testimonials" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Testimonials
        </button>
        <button onClick={() => setTab("rookie")} data-testid="admin-tab-rookie"
          className={`px-5 py-2 font-head uppercase tracking-wide text-sm border-2 transition-colors ${tab === "rookie" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
          Rookie Guide
        </button>
      </div>

      {tab === "write" && <Writer api={adminApi(key)} onChange={() => loadCounts()} />}
      {tab === "scheduled" && <Scheduled api={adminApi(key)} onChange={() => loadCounts()} />}
      {tab === "review" && <ReviewQueue api={adminApi(key)} progress={reviewProgress} onChange={() => loadCounts()} />}
      {tab === "purchases" && <Purchases api={adminApi(key)} />}
      {tab === "downloads" && <Downloads api={adminApi(key)} />}
      {tab === "newsletter" && <NewsletterAdmin api={adminApi(key)} />}
      {tab === "ring" && <RingAdmin api={adminApi(key)} />}
      {tab === "poll" && <PollAdmin api={adminApi(key)} />}
      {tab === "journeys" && <JourneysAdmin api={adminApi(key)} />}
      {tab === "hero" && <HeroAdmin api={adminApi(key)} />}
      {tab === "searches" && <SearchesAdmin api={adminApi(key)} />}
      {tab === "quickplays" && <QuickPlaysAdmin api={adminApi(key)} />}
      {tab === "starterkit" && <StarterKitAdmin api={adminApi(key)} />}
      {tab === "media" && <MediaLibrary api={adminApi(key)} />}
      {tab === "funnel" && <FunnelAdmin api={adminApi(key)} />}
      {tab === "testimonials" && <TestimonialsAdmin api={adminApi(key)} />}
      {tab === "rookie" && <RookieAdmin api={adminApi(key)} />}
    </div>
  );
}

function Writer({ api, onChange }) {
  const [f, setF] = useState({ title: "", category: "Coaches Corner", notes: "", body_html: "", image_url: "", locker_room: false, series: "None", format: "Read" });
  const [mode, setMode] = useState("now");
  const [when, setWhen] = useState(nextWeekday(2));
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [published, setPublished] = useState(null);

  const draft = async (polish) => {
    if (!f.title) { toast.error("Give it a working title first."); return; }
    if (polish && !f.body_html) { toast.error("Nothing to polish yet."); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/scout-draft", {
        title: f.title, category: f.category,
        notes: polish ? null : f.notes || null,
        body_html: polish ? f.body_html : null,
      });
      setF((p) => ({ ...p, body_html: data.body_html }));
      toast.success(polish ? "Scout polished it." : "Scout drafted it.");
    } catch {
      toast.error("Scout couldn't draft that. Try again.");
    } finally {
      setDrafting(false);
    }
  };

  const publish = async () => {
    if (!f.title || !f.body_html) { toast.error("Title and body are required."); return; }
    if (mode === "schedule" && !when) { toast.error("Pick a publish date."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/articles", {
        title: f.title, category: f.category, body_html: f.body_html,
        excerpt: null, image_url: f.image_url || null,
        locker_room: f.locker_room, series: f.series, format: f.format,
        scheduled_for: mode === "schedule" ? when : null,
      });
      setPublished(data);
      toast.success(data.published ? "Published!" : `Scheduled for ${fmtDate(data.scheduled_for)}`);
      setF({ title: "", category: "Coaches Corner", notes: "", body_html: "", image_url: "", locker_room: false, series: "None", format: "Read" });
      setMode("now");
      onChange && onChange();
    } catch {
      toast.error("Publish failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 space-y-5" data-testid="admin-writer">
      {published && (
        <div className="border-2 border-[#2D2D2D] bg-[#E7E3D9] p-4 flex items-center justify-between" data-testid="admin-publish-result">
          <span className="text-sm text-[#1E1E1E]">
            {published.published ? "Published" : `Scheduled for ${fmtDate(published.scheduled_for)}`}: <strong>{published.title}</strong>{published.flagged_for_review && " · flagged for review"}
          </span>
          {published.published && <a href={`/article/${published.slug}`} target="_blank" rel="noreferrer" className="text-[#2D2D2D] link-line text-sm inline-flex items-center gap-1">View <ArrowRight size={14} /></a>}
        </div>
      )}
      <div>
        <label className="kicker">Title</label>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="admin-title-input"
          className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="kicker">Category</label>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="admin-category-select"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="kicker">Image URL (optional)</label>
          <input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="admin-image-input"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="Leave blank to auto-pick" />
          <div className="mt-2 flex items-center gap-3">
            <ImageUpload api={api} testid="admin-image-upload" label="Upload from computer" onUploaded={(m) => setF((p) => ({ ...p, image_url: m.url }))} />
            {f.image_url && <img src={f.image_url} alt="" className="h-10 w-16 object-cover border border-[#D3CFC0]" data-testid="admin-image-preview" />}
          </div>
        </div>
        <div>
          <label className="kicker">Series</label>
          <select value={f.series} onChange={(e) => setF({ ...f, series: e.target.value })} data-testid="admin-series-select"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]">
            {SERIES_OPTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="kicker">Format</label>
          <select value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })} data-testid="admin-format-select"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]">
            {FORMAT_OPTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="admin-lockerroom-label">
        <input type="checkbox" checked={f.locker_room} onChange={(e) => setF({ ...f, locker_room: e.target.checked })}
          data-testid="admin-lockerroom-checkbox" className="w-4 h-4 accent-[#DB9130]" />
        <span className="font-head uppercase tracking-wide text-xs text-[#1E1E1E]">Player content — show in The Locker Room</span>
      </label>

      <div className="border-2 border-[#D3CFC0] p-4">
        <label className="kicker">When</label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button onClick={() => setMode("now")} data-testid="admin-mode-now"
            className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${mode === "now" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>Publish now</button>
          <button onClick={() => setMode("schedule")} data-testid="admin-mode-schedule"
            className={`px-4 py-2 font-head uppercase tracking-wide text-xs border-2 transition-colors ${mode === "schedule" ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>Schedule for later</button>
        </div>
        {mode === "schedule" && (
          <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="admin-schedule-controls">
            <input type="date" value={when} min={ymd(new Date())} onChange={(e) => setWhen(e.target.value)} data-testid="admin-schedule-date"
              className="border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 focus:outline-none focus:border-[#2D2D2D]" />
            <button onClick={() => setWhen(nextWeekday(2))} data-testid="admin-next-tue" className="text-xs font-head uppercase tracking-wide text-[#2D2D2D] link-line">Next Tue</button>
            <button onClick={() => setWhen(nextWeekday(5))} data-testid="admin-next-fri" className="text-xs font-head uppercase tracking-wide text-[#2D2D2D] link-line">Next Fri</button>
            <span className="text-xs text-[#6E6E6E]">Goes live on its own at {when ? fmtDate(when) : "—"}.</span>
          </div>
        )}
      </div>

      <div>
        <label className="kicker">Notes / angle for Scout (optional)</label>
        <textarea rows="2" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="admin-notes-input"
          className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="A bullet or two on what this piece should cover." />
        <div className="mt-2 flex gap-2">
          <button onClick={() => draft(false)} disabled={drafting} data-testid="admin-scout-draft-btn"
            className="inline-flex items-center gap-2 border-2 border-[#E8A33D] text-[#2D2D2D] font-head uppercase tracking-wide text-xs px-4 py-2 hover:bg-[#E8A33D] transition-colors disabled:opacity-60">
            {drafting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Draft with Scout
          </button>
          <button onClick={() => draft(true)} disabled={drafting} data-testid="admin-scout-polish-btn"
            className="inline-flex items-center gap-2 border-2 border-[#D3CFC0] text-[#6E6E6E] font-head uppercase tracking-wide text-xs px-4 py-2 hover:border-[#2D2D2D] transition-colors disabled:opacity-60">
            <Sparkles size={14} /> Polish body
          </button>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between gap-3">
          <label className="kicker">Body (HTML)</label>
          <ImageUpload api={api} testid="admin-body-image" label="Insert image" onUploaded={(m) => setF((p) => ({ ...p, body_html: `${p.body_html}\n<figure><img src="${m.url}" alt="${(m.alt || "").replace(/"/g, "&quot;")}" /></figure>` }))} />
        </div>
        <textarea rows="16" value={f.body_html} onChange={(e) => setF({ ...f, body_html: e.target.value })} data-testid="admin-body-input"
          className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#2D2D2D]" placeholder="<p>Write here, or let Scout draft it…</p>" />
      </div>
      {f.body_html && (
        <div>
          <label className="kicker">Preview</label>
          <div className="mt-1 border-2 border-[#D3CFC0] bg-[#F3F1EC] p-5 article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(f.body_html) }} data-testid="admin-preview" />
        </div>
      )}
      <button onClick={publish} disabled={busy} data-testid="admin-publish-btn"
        className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
        {busy ? "Saving…" : mode === "schedule" ? "Schedule article" : "Publish article"}
      </button>
    </div>
  );
}

function Scheduled({ api, onChange }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/scheduled");
    setItems(data.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const publishNow = async (slug) => {
    setBusy(slug);
    try { await api.post(`/admin/articles/${slug}/publish`); toast.success("Published."); await load(); onChange && onChange(); }
    catch { toast.error("Publish failed."); } finally { setBusy(""); }
  };
  const remove = async (slug) => {
    if (!window.confirm("Delete this scheduled draft? This can't be undone.")) return;
    setBusy(slug);
    try { await api.delete(`/admin/articles/${slug}`); toast.success("Deleted."); await load(); onChange && onChange(); }
    catch { toast.error("Delete failed."); } finally { setBusy(""); }
  };

  if (!items) return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>;
  if (items.length === 0) return (
    <div className="mt-10 border-2 border-[#D3CFC0] bg-[#E7E3D9] p-8 text-center" data-testid="scheduled-empty">
      <p className="font-head uppercase text-[#1E1E1E]">Nothing scheduled</p>
      <p className="text-sm text-[#6E6E6E] mt-1">Write a post and choose "Schedule for later" to line up your Tue/Fri drops.</p>
    </div>
  );

  return (
    <div className="mt-8 divide-y divide-[#D3CFC0] border-2 border-[#D3CFC0]" data-testid="scheduled-list">
      {items.map((a) => (
        <div key={a.slug} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`scheduled-row-${a.slug}`}>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="kicker text-[#E8A33D]">{fmtDate(a.scheduled_for)}</span>
              <span className="kicker text-[#6E6E6E]">· {a.category}</span>
            </div>
            <h3 className="font-head font-semibold text-lg text-[#1E1E1E] mt-0.5">{a.title}</h3>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => publishNow(a.slug)} disabled={busy === a.slug} data-testid={`scheduled-publish-${a.slug}`}
              className="inline-flex items-center gap-1 bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-xs px-4 py-2 hover:bg-[#242424] disabled:opacity-60"><Check size={14} /> Publish now</button>
            <button onClick={() => remove(a.slug)} disabled={busy === a.slug} data-testid={`scheduled-delete-${a.slug}`}
              className="inline-flex items-center gap-1 border-2 border-[#D3CFC0] text-[#6E6E6E] font-head uppercase tracking-wide text-xs px-4 py-2 hover:border-[#B44] hover:text-[#B44]"><Trash2 size={14} /> Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewQueue({ api, progress, onChange }) {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ title: "", body_html: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/review-queue");
    setItems(data.items);
    setIdx(0);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!items) return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading queue…</div>;

  const pct = progress.total ? Math.round((progress.reviewed / progress.total) * 100) : 100;
  const bar = (
    <div className="mt-8 mb-6" data-testid="review-progress">
      <div className="flex items-end justify-between mb-2">
        <span className="kicker">Mental-health review progress</span>
        <span className="font-head uppercase tracking-wide text-sm text-[#1E1E1E]" data-testid="review-progress-label">
          {progress.reviewed} of {progress.total} reviewed
        </span>
      </div>
      <div className="h-3 w-full bg-[#D3CFC0] border-2 border-[#1E1E1E]">
        <div className="h-full bg-[#E8A33D] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="review-progress-fill" />
      </div>
      <p className="mt-1.5 text-xs text-[#6E6E6E]">{progress.total - progress.reviewed} left in the queue.</p>
    </div>
  );

  if (items.length === 0) return (
    <div>
      {bar}
      <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-8 text-center" data-testid="review-empty"><Check className="mx-auto text-[#E8A33D]" size={36} /><p className="mt-3 font-head uppercase text-[#1E1E1E]">Queue clear</p><p className="text-sm text-[#6E6E6E] mt-1">Every flagged article has been reviewed.</p></div>
    </div>
  );

  const a = items[Math.min(idx, items.length - 1)];

  const afterAction = async () => { await load(); onChange && onChange(); setEditing(false); };

  const approve = async () => { setBusy(true); try { await api.post(`/admin/articles/${a.slug}/approve`); toast.success("Approved & published."); await afterAction(); } catch { toast.error("Approve failed."); } finally { setBusy(false); } };
  const unpublish = async () => { setBusy(true); try { await api.post(`/admin/articles/${a.slug}/unpublish`); toast.success("Unpublished."); await afterAction(); } catch { toast.error("Unpublish failed."); } finally { setBusy(false); } };
  const startEdit = () => { setEdit({ title: a.title, body_html: a.body_html }); setEditing(true); };
  const polish = async () => { setBusy(true); try { const { data } = await api.post("/admin/scout-draft", { title: edit.title, category: a.category, body_html: edit.body_html }); setEdit((p) => ({ ...p, body_html: data.body_html })); toast.success("Scout polished it."); } catch { toast.error("Polish failed."); } finally { setBusy(false); } };
  const saveEdit = async () => { setBusy(true); try { await api.patch(`/admin/articles/${a.slug}`, { title: edit.title, body_html: edit.body_html }); toast.success("Saved."); await afterAction(); } catch { toast.error("Save failed."); } finally { setBusy(false); } };

  return (
    <div data-testid="review-queue">
      {bar}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#6E6E6E] font-head uppercase tracking-wide">Reviewing {idx + 1} of {items.length}</span>
        <div className="flex gap-2">
          <button onClick={() => { setEditing(false); setIdx(Math.max(0, idx - 1)); }} disabled={idx === 0} data-testid="review-prev" className="p-2 border-2 border-[#D3CFC0] text-[#6E6E6E] disabled:opacity-30 hover:border-[#2D2D2D]"><ArrowLeft size={16} /></button>
          <button onClick={() => { setEditing(false); setIdx(Math.min(items.length - 1, idx + 1)); }} disabled={idx >= items.length - 1} data-testid="review-next" className="p-2 border-2 border-[#D3CFC0] text-[#6E6E6E] disabled:opacity-30 hover:border-[#2D2D2D]"><ArrowRight size={16} /></button>
        </div>
      </div>

      <div className="border-2 border-[#E8A33D] bg-[#E7E3D9] p-3 text-xs text-[#6E6E6E] mb-4">
        Flagged as mental-health-adjacent — review before it stays live. Approve to clear the flag, edit the copy, or unpublish it.
      </div>

      <div className="border-2 border-[#1E1E1E] bg-[#F3F1EC]">
        <div className="p-5 border-b-2 border-[#D3CFC0]">
          <span className="kicker">{a.category}</span>
          {editing ? (
            <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="review-edit-title"
              className="mt-2 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 font-head text-xl focus:outline-none focus:border-[#2D2D2D]" />
          ) : (
            <h2 className="mt-1 font-head font-semibold text-2xl text-[#1E1E1E]" data-testid="review-title">{a.title}</h2>
          )}
        </div>
        <div className="p-5 max-h-[440px] overflow-auto">
          {editing ? (
            <textarea rows="16" value={edit.body_html} onChange={(e) => setEdit({ ...edit, body_html: e.target.value })} data-testid="review-edit-body"
              className="w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#2D2D2D]" />
          ) : (
            <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body_html) }} data-testid="review-body" />
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        {editing ? (
          <>
            <button onClick={saveEdit} disabled={busy} data-testid="review-save-btn" className="inline-flex items-center gap-2 bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#242424] disabled:opacity-60"><Check size={16} /> Save & approve later</button>
            <button onClick={polish} disabled={busy} data-testid="review-polish-btn" className="inline-flex items-center gap-2 border-2 border-[#E8A33D] text-[#2D2D2D] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#E8A33D]"><Sparkles size={16} /> Polish with Scout</button>
            <button onClick={() => setEditing(false)} className="text-[#6E6E6E] font-head uppercase tracking-wide text-sm px-3">Cancel</button>
          </>
        ) : (
          <>
            <button onClick={approve} disabled={busy} data-testid="review-approve-btn" className="inline-flex items-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#D6922C] disabled:opacity-60"><Check size={16} /> Approve</button>
            <button onClick={startEdit} disabled={busy} data-testid="review-edit-btn" className="inline-flex items-center gap-2 border-2 border-[#2D2D2D] text-[#2D2D2D] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:bg-[#2D2D2D] hover:text-[#F3F1EC]"><Pencil size={16} /> Edit</button>
            <button onClick={unpublish} disabled={busy} data-testid="review-unpublish-btn" className="inline-flex items-center gap-2 border-2 border-[#D3CFC0] text-[#6E6E6E] font-head uppercase tracking-wide text-sm px-5 py-2.5 hover:border-[#B44] hover:text-[#B44]"><Trash2 size={16} /> Unpublish</button>
          </>
        )}
      </div>
    </div>
  );
}


function money(cents, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format((cents || 0) / 100);
}

function Purchases({ api }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/analytics"); setData(data); }
      catch { toast.error("Couldn't load purchases."); setData({ error: true }); }
    })();
    // eslint-disable-next-line
  }, []);

  if (!data) return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading purchases…</div>;
  if (data.error) return <div className="mt-10 text-[#6E6E6E]">Couldn't load purchase data.</div>;

  const maxRev = Math.max(1, ...(data.top_products || []).map((p) => p.revenue));

  return (
    <div className="mt-8 space-y-8" data-testid="admin-purchases">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9] p-5" data-testid="stat-revenue">
          <div className="flex items-center gap-2 text-[#6E6E6E]"><DollarSign size={16} /><span className="kicker">Total revenue</span></div>
          <p className="mt-2 font-head font-semibold text-3xl text-[#1E1E1E]">{money(data.revenue_cents)}</p>
        </div>
        <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9] p-5" data-testid="stat-orders">
          <div className="flex items-center gap-2 text-[#6E6E6E]"><ShoppingCart size={16} /><span className="kicker">Paid orders</span></div>
          <p className="mt-2 font-head font-semibold text-3xl text-[#1E1E1E]">{data.order_count}</p>
        </div>
        <div className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-5" data-testid="stat-abandoned">
          <div className="flex items-center gap-2 text-[#6E6E6E]"><Package size={16} /><span className="kicker">Open checkouts</span></div>
          <p className="mt-2 font-head font-semibold text-3xl text-[#1E1E1E]">{data.abandoned_open}</p>
        </div>
      </div>

      <div>
        <h3 className="font-head font-semibold uppercase text-xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2 mb-4">Top sellers</h3>
        {(data.top_products || []).length === 0 ? (
          <p className="text-sm text-[#6E6E6E]" data-testid="top-products-empty">No sales yet.</p>
        ) : (
          <div className="space-y-3" data-testid="top-products">
            {data.top_products.map((p) => (
              <div key={p.lookup_key} data-testid={`top-product-${p.lookup_key}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#1E1E1E] font-medium truncate pr-3">{p.name}</span>
                  <span className="text-[#6E6E6E] shrink-0 font-head uppercase tracking-wide text-xs">{p.units} sold · {money(p.revenue)}</span>
                </div>
                <div className="mt-1 h-2.5 w-full bg-[#D3CFC0]">
                  <div className="h-full bg-[#2D2D2D]" style={{ width: `${Math.round((p.revenue / maxRev) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-head font-semibold uppercase text-xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2 mb-4">Recent orders</h3>
        {(data.recent_orders || []).length === 0 ? (
          <p className="text-sm text-[#6E6E6E]" data-testid="recent-orders-empty">No orders yet — they'll show up here as they come in.</p>
        ) : (
          <div className="divide-y divide-[#D3CFC0] border-2 border-[#D3CFC0]" data-testid="recent-orders">
            {data.recent_orders.map((o, i) => (
              <div key={i} className="p-4 flex items-center justify-between gap-3" data-testid={`recent-order-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm text-[#1E1E1E] font-medium truncate">{o.name}</p>
                  <p className="text-xs text-[#6E6E6E] truncate">{o.email || "—"} · {o.at ? fmtDate(o.at) : ""}</p>
                </div>
                <span className="font-head uppercase tracking-wide text-sm text-[#1E1E1E] shrink-0">{money(o.amount, o.currency)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function bytesHuman(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

function Downloads({ api }) {
  const [products, setProducts] = useState(null);
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(0);

  const load = async () => {
    const [p, f] = await Promise.all([api.get("/products"), api.get("/admin/product-files")]);
    setProducts((p.data || []).filter((x) => x.checkout !== "page"));
    setFiles(f.data.files || {});
  };
  useEffect(() => { load().catch(() => toast.error("Couldn't load products.")); /* eslint-disable-next-line */ }, []);

  const upload = async (lookup_key, file) => {
    if (!file) return;
    setBusy(lookup_key);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/products/${lookup_key}/file`, fd, {
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success("File uploaded — buyers now get an instant download.");
      await load();
    } catch (err) {
      toast.error(err?.code === "ECONNABORTED" ? "Upload timed out — try a smaller file or check your connection." : "Upload failed. Try again.");
    } finally {
      setBusy("");
      setProgress(0);
    }
  };

  if (!products) return <div className="mt-10 text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading products…</div>;

  return (
    <div className="mt-8 space-y-4" data-testid="admin-downloads">
      <p className="text-sm text-[#6E6E6E] max-w-2xl">
        Upload the digital file (PDF, ZIP, etc.) for each product. Buyers get a secure 24-hour download link on the
        success page and in their receipt email. Re-uploading replaces the current file.
      </p>
      {products.filter((p) => !p.is_bundle).map((p) => {
        const f = files[p.lookup_key];
        const uploading = busy === p.lookup_key;
        return (
          <div key={p.lookup_key} data-testid={`download-row-${p.lookup_key}`}
            className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-head font-semibold text-[#1E1E1E] leading-tight">{p.name}</h3>
              <p className="text-xs text-[#6E6E6E] mt-0.5">${p.amount / 100} · {p.lookup_key}</p>
              {uploading ? (
                <div className="mt-2" data-testid={`download-progress-${p.lookup_key}`}>
                  <div className="flex items-center justify-between text-xs text-[#2D2D2D] font-head uppercase tracking-wide">
                    <span>{progress < 100 ? `Uploading… ${progress}%` : "Finalizing…"}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full bg-[#D3CFC0] overflow-hidden">
                    <div className="h-full bg-[#E8A33D] transition-all duration-200"
                      style={{ width: `${progress < 100 ? progress : 100}%` }} />
                  </div>
                </div>
              ) : f ? (
                <p className="mt-1 text-xs text-[#2E7D57] flex items-center gap-1" data-testid={`download-status-${p.lookup_key}`}>
                  <Check size={13} /> {f.filename} <span className="text-[#6E6E6E]">({bytesHuman(f.size)})</span>
                </p>
              ) : (
                <p className="mt-1 text-xs text-[#B4443A]" data-testid={`download-status-${p.lookup_key}`}>No file uploaded yet</p>
              )}
            </div>
            <div className="shrink-0">
              <label className={`inline-flex items-center gap-2 px-4 py-2 font-head uppercase tracking-wide text-xs transition-colors ${uploading ? "bg-[#D3CFC0] text-[#6E6E6E] cursor-wait" : "bg-[#2D2D2D] text-[#F3F1EC] hover:bg-[#1E1E1E] cursor-pointer"}`} data-testid={`download-upload-label-${p.lookup_key}`}>
                {uploading ? <Loader2 className="animate-spin" size={14} /> : <ArrowRight size={14} />}
                {uploading ? (progress < 100 ? `${progress}%` : "Finalizing…") : (f ? "Replace file" : "Upload file")}
                <input type="file" className="hidden" disabled={uploading}
                  data-testid={`download-input-${p.lookup_key}`}
                  onChange={(e) => upload(p.lookup_key, e.target.files?.[0])} />
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
}
